import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { backofficeUrl } from 'src/shared/helpers';
import { EnvType } from 'src/shared/hooks';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { useLastUsedTool } from '../../../shared/hooks';
import AngleDown from 'src/shared/svg/angleDown';
import styled from 'styled-components';
import { Icon } from '../icon';
import { getTools } from '../toolbar/tools';

// Solution switcher moved from topbar. Sits at the top of the sidebar and
// lets the user jump between Visual Product Customizer, 3D Product
// Configurator, 3D DAM & AR Viewer, AI Agents Studio.

const Container = styled.div`
    position: relative;
    margin: 0 12px 10px;
`;

const Trigger = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    background: #f9f9f9;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    color: #333;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.07);
    }
`;

const TriggerIcon = styled.div`
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const TriggerLabel = styled.span`
    flex: 1;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TriggerArrow = styled.span<{ $isOpen: boolean }>`
    display: inline-flex;
    transform: rotate(${(p) => (p.$isOpen ? '180deg' : '0deg')});
    transition: transform 0.2s ease;

    svg {
        width: 10px;
        height: 10px;
    }
`;

const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    z-index: 100;
    overflow: hidden;
`;

const Option = styled.div<{ $isActive: boolean }>`
    display: flex;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;
    background: ${(p) => (p.$isActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent')};
    transition: background 0.1s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.08);
    }
`;

const OptionIcon = styled.div`
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const OptionBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const OptionName = styled.strong`
    font-size: 12px;
    font-weight: 600;
    color: #333;
`;

const OptionDescription = styled.span`
    font-size: 10px;
    color: #777;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const SolutionSwitcher: FunctionComponent = () => {
    const { state } = useAppState();
    const { setLastUsedTool } = useLastUsedTool();
    const { userInfo, ecommerceSettings } = useAccountData();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const userID = userInfo?.userID;
    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: (userID || 0).toString()
    });

    const tools = getTools(isNewConfiguratorBackoffice);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const getActiveToolType = (): string => {
        if (isNewConfiguratorBackoffice) {
            switch (state.lastUsedTool) {
                case 1:
                    return 'CUSTOMIZER';
                case 2:
                    return 'CONFIGURATOR';
                case 3:
                    return 'VIEWER';
                default:
                    return 'CUSTOMIZER';
            }
        }
        const url = window.location.href;
        if (url.indexOf('landings/configurator') !== -1 || url.indexOf('/Composer') !== -1) return 'CONFIGURATOR';
        if (url.indexOf('landings/viewer') !== -1 || url.indexOf('Admin/DAM') !== -1) return 'VIEWER';
        return 'CUSTOMIZER';
    };

    const activeToolType = getActiveToolType();
    const activeTool = tools.find((t) => t.type === activeToolType);
    const isWix = ecommerceSettings?.ecommerceName?.toUpperCase() === 'WIX';

    if (!userID) return null;

    const handleSelect = (tool: (typeof tools)[number]) => {
        setIsOpen(false);
        if (tool.type === 'AI_STUDIO') {
            navigate(backofficeUrl('/ai-creative-tools/studio'));
            return;
        }
        if (tool.type === 'CUSTOMIZER' || tool.type === 'VIEWER' || tool.type === 'CONFIGURATOR_2') {
            setLastUsedTool(tool.type as EnvType);
        }
        window.location.href = tool.url;
    };

    return (
        <Container ref={containerRef}>
            <Trigger onClick={() => setIsOpen(!isOpen)} data-testid='sidebar-solution-switcher'>
                <TriggerIcon>
                    <Icon>{activeTool?.icon}</Icon>
                </TriggerIcon>
                <TriggerLabel>{activeTool?.name}</TriggerLabel>
                <TriggerArrow $isOpen={isOpen}>
                    <AngleDown />
                </TriggerArrow>
            </Trigger>
            {isOpen && (
                <Dropdown>
                    {tools.map((tool) => {
                        if (isWix && tool.type === 'CONFIGURATOR') return null;
                        return (
                            <Option key={tool.type} $isActive={tool.type === activeToolType} onClick={() => handleSelect(tool)}>
                                <OptionIcon>
                                    <Icon>{tool.icon}</Icon>
                                </OptionIcon>
                                <OptionBody>
                                    <OptionName>{tool.name}</OptionName>
                                    <OptionDescription>{tool.description}</OptionDescription>
                                </OptionBody>
                            </Option>
                        );
                    })}
                </Dropdown>
            )}
        </Container>
    );
};

export default SolutionSwitcher;
